/**
 * The two steps of signing in, and the end of a session.
 *
 * Nothing here reads the admin back afterwards. There is no route that
 * describes one — see `useAdmin` — and there is no need for one either: the
 * second step below answers with who signed in, and the session keeps it.
 */

import { API_ENDPOINTS, api } from "../../../lib/api";
import type {
  LoginResult,
  RefreshTokenResult,
  VerifyOtpResult,
} from "../types/auth.types";

/**
 * Step one: the address and the password.
 *
 * On success the server has mailed a one-time code and the screen moves to the
 * second step. A wrong password is an `ApiError` carrying the server's own
 * wording, which is what the card prints under the box.
 */
export const login = async (
  email: string,
  password: string
): Promise<LoginResult> =>
  api.post<LoginResult>(API_ENDPOINTS.auth.login, { email, password });

/* There used to be a `verifyCaptcha` here, for a "click to verify" box that
   sat between the password and the submit. The box is gone from the design —
   the second factor is what actually stops a password being guessed at, and a
   challenge in front of it was asking staff to prove they were human twice on
   the way into their own console. `API_ENDPOINTS.auth.verifyCaptcha` went with
   it; both are in the history if the box ever comes back. */

/** Step two: the six digits, and the session they earn. */
export const verifyOtp = async (
  email: string,
  otp: string
): Promise<VerifyOtpResult> =>
  api.post<VerifyOtpResult>(API_ENDPOINTS.auth.verifyOtp, {
    email,
    code: otp,
  });

/** Trades a refresh token for a new pair. Used by the API client itself to
 *  keep a session alive past its access token's short lifetime — see
 *  `lib/api/client.ts` — so this is rarely called by a screen directly. */
export const refreshToken = async (
  refreshTokenValue: string
): Promise<RefreshTokenResult> =>
  api.post<RefreshTokenResult>(API_ENDPOINTS.auth.refreshToken, {
    refresh_token: refreshTokenValue,
  });

/** Ends the session on the server, so the refresh token being signed out
 *  with cannot be replayed. The access token rides along automatically —
 *  see `lib/api/client.ts`. */
export const logout = async (refreshTokenValue: string): Promise<void> => {
  await api.post(API_ENDPOINTS.auth.logout, {
    refresh_token: refreshTokenValue,
  });
};
